import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

/**
 * MissionStory — the Genesis 2:7 / dust-of-the-earth section.
 * Split editorial layout with a slow-parallax hands/soil image.
 */

export default function MissionStory() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      ref={ref}
      id="story"
      className="relative bg-[#1a1412] py-28 md:py-40 border-t border-[#362b28] overflow-hidden"
      data-testid="mission-story-section"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8 md:gap-12 items-center">
        <div className="col-span-12 md:col-span-6 order-2 md:order-1">
          <p className="eyebrow mb-6">EarthTone · Genesis 2:7</p>
          <motion.h2
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
            className="font-serif text-[42px] md:text-[68px] leading-[0.98] tracking-tight text-[#e8dcc4]"
          >
            We are not a color. <br />
            <em className="italic text-[#c28b46]">We are EarthTone.</em>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="mt-8 md:mt-12 space-y-6 text-[16px] md:text-[17px] leading-relaxed text-[#c8b99c] max-w-[52ch]"
          >
            <p>
              Genesis 2:7 says the Lord God <em className="text-[#e8dcc4]">formed man of the dust of the ground</em>. Not from something abstract. From soil. From earth. And the ground where the first bodies were formed is <strong className="text-[#e8dcc4]">EarthTone</strong> — the color of the ground when the sun sets across it.
            </p>
            <p>
              We were never &ldquo;Black.&rdquo; That word was placed on us. Long before the label existed, scripture had already named us: dust, ground, earth. <em className="text-[#c28b46]">EarthTone Original Skin</em>. Melanin is not a marker of inferiority — it is biological technology. The Creator&rsquo;s fingerprint pressed into the first bodies.
            </p>
            <p className="pt-2 border-l-2 border-[#c28b46] pl-5 font-serif italic text-[#e8dcc4] text-[18px] md:text-[20px]">
              This clothing is a reminder. That is all. Everything else — the science, the scripture, the story — you already carry.
            </p>
          </motion.div>
        </div>

        <div className="col-span-12 md:col-span-6 order-1 md:order-2">
          <div className="relative overflow-hidden border border-[#362b28] aspect-[4/5] md:aspect-[3/4]">
            <motion.img
              style={{ y: imgY }}
              src="/brand/founder-baobabs.webp"
              alt="EarthTone founder standing among baobabs at sunset"
              className="absolute inset-0 w-full h-[115%] object-cover object-[50%_35%]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a1412]/60 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <p className="font-serif italic text-[#f0e4cc] text-[18px] md:text-[22px] leading-snug">
                “...formed man of the dust of the ground.”
              </p>
              <p className="mt-2 text-[10px] tracking-[0.32em] uppercase text-[#c28b46]">
                Genesis 2:7
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
